import { unzlibSync, zlibSync } from 'fflate';

/**
 * Thrown by `decodeText` when the payload is malformed (bad base64url,
 * bad zlib frame, etc.). Catch this to surface a user-facing error.
 */
export class ShareDecodeError extends Error {
    readonly kind = 'ShareDecodeError' as const;

    constructor(message: string, cause?: unknown) {
        super(message);
        this.name = 'ShareDecodeError';
        if (cause !== undefined) this.cause = cause;
    }
}

// ── base64url helpers ────────────────────────────────────────────────────────

/** Converts a standard base64 string to base64url (RFC 4648 §5). */
function toBase64Url(b64: string): string {
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Converts a base64url string back to padded standard base64. */
function fromBase64Url(b64url: string): string {
    const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
    const pad = (4 - (b64.length % 4)) % 4;
    return b64 + '='.repeat(pad);
}

// ── public encoding API ──────────────────────────────────────────────────────

/**
 * Encodes Nowline source text as `base64url(zlib(utf8(source)))`.
 *
 * The zlib frame is RFC 1950 (produced by fflate `zlibSync`, byte-compatible
 * with native `CompressionStream('deflate')`). This is the canonical
 * OSS-owned grammar for `#text=` share URLs.
 */
export function encodeText(source: string): string {
    const bytes = new TextEncoder().encode(source);
    const compressed = zlibSync(bytes);
    // Uint8Array → binary string for btoa
    const binary = Array.from(compressed, (b) => String.fromCharCode(b)).join('');
    return toBase64Url(btoa(binary));
}

/**
 * Decodes a `#text=<payload>` payload back to source text.
 *
 * @throws {ShareDecodeError} if the payload is not valid base64url or the
 *   zlib frame is malformed.
 */
export function decodeText(payload: string): string {
    let binary: string;
    try {
        binary = atob(fromBase64Url(payload));
    } catch (cause) {
        throw new ShareDecodeError('Invalid base64url payload', cause);
    }
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    let decompressed: Uint8Array;
    try {
        decompressed = unzlibSync(bytes);
    } catch (cause) {
        throw new ShareDecodeError('Invalid zlib payload', cause);
    }
    return new TextDecoder().decode(decompressed);
}

// ── hash parser ──────────────────────────────────────────────────────────────

/** Discriminated union returned by `parseLocationHash`. */
export type ParsedHash =
    | { kind: 'text'; payload: string }
    | { kind: 'url'; url: string }
    | { kind: 'none' };

/**
 * Parses `location.hash` into a typed discriminated union.
 *
 * Receiver precedence (OSS grammar): `#text=` → `#url=` → none.
 * Pass `location.hash` directly; the leading `#` is stripped automatically.
 */
export function parseLocationHash(hash: string): ParsedHash {
    const stripped = hash.startsWith('#') ? hash.slice(1) : hash;
    if (stripped.startsWith('text=')) {
        return { kind: 'text', payload: stripped.slice('text='.length) };
    }
    if (stripped.startsWith('url=')) {
        return { kind: 'url', url: decodeURIComponent(stripped.slice('url='.length)) };
    }
    return { kind: 'none' };
}
